// ─────────────────────────────────────────────────
// Minesweeper.jsx — 지뢰찾기 이스터에그 미니게임
// 좌클릭: 공개 / 우클릭: 깃발 토글
// 첫 클릭은 항상 안전 (지뢰 자동 회피)
// ─────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const DIFFICULTIES = {
  easy:   { name: '쉬움',   rows: 9,  cols: 9,  mines: 10 },
  medium: { name: '보통',   rows: 16, cols: 16, mines: 40 },
  hard:   { name: '어려움', rows: 16, cols: 24, mines: 80 }
};

const NUM_COLORS = ['', '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#f57c00', '#0097a7', '#5d4037', '#212121'];

const cellSize = (cols) => cols <= 9 ? 34 : cols <= 16 ? 30 : 26;
const fontSize = (cols) => cols <= 9 ? 16 : cols <= 16 ? 14 : 12;

function makeEmptyBoard(rows, cols) {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({
      mine: false,
      revealed: false,
      flagged: false,
      neighbor: 0
    }))
  );
}

function placeMines(board, rows, cols, mineCount, safeR, safeC) {
  // 첫 클릭한 칸과 그 주변 8칸은 지뢰 배치 제외 (안전 영역)
  const safeKey = (r, c) => `${r},${c}`;
  const safe = new Set();
  for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
    safe.add(safeKey(safeR + dr, safeC + dc));
  }
  let placed = 0;
  while (placed < mineCount) {
    const r = Math.floor(Math.random() * rows);
    const c = Math.floor(Math.random() * cols);
    if (board[r][c].mine) continue;
    if (safe.has(safeKey(r, c))) continue;
    board[r][c].mine = true;
    placed++;
  }
  // 주변 지뢰 수 계산
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (board[r][c].mine) continue;
    let n = 0;
    for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && board[nr][nc].mine) n++;
    }
    board[r][c].neighbor = n;
  }
  return board;
}

function floodReveal(board, rows, cols, startR, startC) {
  const stack = [[startR, startC]];
  while (stack.length) {
    const [r, c] = stack.pop();
    if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
    const cell = board[r][c];
    if (cell.revealed || cell.flagged || cell.mine) continue;
    cell.revealed = true;
    if (cell.neighbor === 0) {
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        stack.push([r + dr, c + dc]);
      }
    }
  }
  return board;
}

export default function Minesweeper({ open, onClose }) {
  const [difficulty, setDifficulty] = useState('easy');
  const cfg = DIFFICULTIES[difficulty];
  const [board, setBoard] = useState(() => makeEmptyBoard(cfg.rows, cfg.cols));
  const [status, setStatus] = useState('idle'); // 'idle' | 'playing' | 'won' | 'lost'
  const [firstClick, setFirstClick] = useState(true);
  const [flagsUsed, setFlagsUsed] = useState(0);
  const [seconds, setSeconds] = useState(0);

  const reset = useCallback((diff) => {
    const c = DIFFICULTIES[diff || difficulty];
    setBoard(makeEmptyBoard(c.rows, c.cols));
    setStatus('idle');
    setFirstClick(true);
    setFlagsUsed(0);
    setSeconds(0);
  }, [difficulty]);

  // 난이도 변경 시 보드 리셋
  useEffect(() => { reset(difficulty); /* eslint-disable-next-line */ }, [difficulty]);

  // 타이머
  useEffect(() => {
    if (status !== 'playing') return;
    const id = setInterval(() => setSeconds(s => Math.min(999, s + 1)), 1000);
    return () => clearInterval(id);
  }, [status]);

  // Esc 종료
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleCellClick = (r, c) => {
    if (status === 'won' || status === 'lost') return;
    const cell = board[r][c];
    if (cell.revealed || cell.flagged) return;

    let next = board.map(row => row.map(cc => ({ ...cc })));
    if (firstClick) {
      next = placeMines(next, cfg.rows, cfg.cols, cfg.mines, r, c);
      setFirstClick(false);
      setStatus('playing');
    }
    const target = next[r][c];
    if (target.mine) {
      // 게임 오버 — 모든 지뢰 공개
      for (let i = 0; i < cfg.rows; i++) for (let j = 0; j < cfg.cols; j++) {
        if (next[i][j].mine) next[i][j].revealed = true;
      }
      next[r][c].triggered = true; // 폭발한 지뢰 표시
      setBoard(next);
      setStatus('lost');
      return;
    }
    floodReveal(next, cfg.rows, cfg.cols, r, c);
    // 승리 체크: 모든 비-지뢰 셀이 공개되었는가?
    let safeRemaining = 0;
    for (let i = 0; i < cfg.rows; i++) for (let j = 0; j < cfg.cols; j++) {
      if (!next[i][j].mine && !next[i][j].revealed) safeRemaining++;
    }
    setBoard(next);
    if (safeRemaining === 0) {
      // 모든 지뢰에 깃발 자동 표시
      const final = next.map(row => row.map(cc => cc.mine ? { ...cc, flagged: true } : cc));
      setBoard(final);
      setFlagsUsed(cfg.mines);
      setStatus('won');
    }
  };

  const handleRightClick = (e, r, c) => {
    e.preventDefault();
    if (status === 'won' || status === 'lost') return;
    const cell = board[r][c];
    if (cell.revealed) return;
    const next = board.map(row => row.map(cc => ({ ...cc })));
    const wasFlagged = next[r][c].flagged;
    next[r][c].flagged = !wasFlagged;
    setBoard(next);
    setFlagsUsed(prev => wasFlagged ? prev - 1 : prev + 1);
    if (status === 'idle') setStatus('playing'); // 깃발만으로도 게임 시작 처리
  };

  const minesLeft = cfg.mines - flagsUsed;
  const cs = cellSize(cfg.cols);
  const fs = fontSize(cfg.cols);

  // 게임 보드를 메모이즈 (성능)
  const boardCells = useMemo(() => board.map((row, r) => (
    <div key={r} style={{display:'flex'}}>
      {row.map((cell, c) => {
        const isReveal = cell.revealed;
        const showMine = isReveal && cell.mine;
        const showNum = isReveal && !cell.mine && cell.neighbor > 0;
        const bg = isReveal
          ? (cell.triggered ? '#ef4444' : '#e5e7eb')
          : '#cbd5e1';
        const bgHover = !isReveal && !cell.flagged ? '#94a3b8' : null;
        return (
          <button
            key={c}
            onClick={() => handleCellClick(r, c)}
            onContextMenu={(e) => handleRightClick(e, r, c)}
            disabled={status === 'won' || status === 'lost'}
            style={{
              width: cs, height: cs, margin: 1,
              border: isReveal ? '1px solid #cbd5e1' : '1px solid #94a3b8',
              borderRadius: 3,
              background: bg,
              cursor: (status === 'won' || status === 'lost') ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: fs, fontWeight: 700, padding: 0, userSelect: 'none',
              color: showNum ? NUM_COLORS[cell.neighbor] : '#0b1c30',
              boxShadow: isReveal ? 'inset 0 1px 2px rgba(0,0,0,0.05)' : '0 1px 0 rgba(255,255,255,0.5), 0 -1px 0 rgba(0,0,0,0.08)',
              transition: 'background 0.08s'
            }}
            onMouseEnter={e => { if (bgHover) e.currentTarget.style.background = bgHover; }}
            onMouseLeave={e => { e.currentTarget.style.background = bg; }}
          >
            {cell.flagged && !isReveal && '🚩'}
            {showMine && (cell.triggered ? '💥' : '💣')}
            {showNum && cell.neighbor}
          </button>
        );
      })}
    </div>
  )), [board, status, cs, fs]); // handlers 의존성 생략

  if (!open) return null;

  const faceEmoji = status === 'lost' ? '😵' : status === 'won' ? '😎' : '🙂';

  return (
    <div
      onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.7)',backdropFilter:'blur(6px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20,animation:'fadeIn 0.3s ease-out'}}>
      <div
        onClick={e => e.stopPropagation()}
        onContextMenu={e => e.preventDefault()}
        style={{background:'#f1f5f9',borderRadius:18,padding:20,boxShadow:'0 30px 80px rgba(0,0,0,0.5)',maxWidth:'95vw',maxHeight:'95vh',overflow:'auto'}}>
        {/* 헤더 */}
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14,flexWrap:'wrap'}}>
          <div style={{fontSize:22,fontWeight:800,color:'#0b1c30',letterSpacing:'-0.02em'}}>💣 지뢰찾기</div>
          <div style={{flex:1}}/>
          {/* 난이도 토글 */}
          <div style={{display:'flex',background:'#fff',border:'1px solid #cbd5e1',borderRadius:9,padding:3}}>
            {Object.entries(DIFFICULTIES).map(([key, c]) => {
              const active = difficulty === key;
              return (
                <button key={key}
                  onClick={() => setDifficulty(key)}
                  style={{padding:'6px 12px',border:'none',background:active?'#0b1c30':'transparent',color:active?'#fff':'#45464d',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',transition:'all 0.12s'}}>
                  {c.name}
                </button>
              );
            })}
          </div>
          <button onClick={onClose} style={{width:34,height:34,borderRadius:8,border:'1px solid #cbd5e1',background:'#fff',cursor:'pointer',fontSize:16,color:'#45464d'}}>✕</button>
        </div>

        {/* 상태 바: 지뢰 / 얼굴 / 타이머 */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,padding:'10px 14px',background:'#0b1c30',borderRadius:10,color:'#fff5e6'}}>
          <div style={{display:'flex',alignItems:'center',gap:6,fontFamily:'monospace',fontWeight:700,fontSize:18}}>
            <span style={{fontSize:14}}>💣</span>
            <span style={{minWidth:40,textAlign:'right',color:minesLeft<0?'#ef4444':'#fff5e6'}}>{String(minesLeft).padStart(3,'0')}</span>
          </div>
          <button onClick={() => reset()} title="다시 시작"
            style={{width:42,height:42,fontSize:24,border:'none',borderRadius:10,background:'#fff5e6',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'transform 0.15s'}}
            onMouseDown={e => e.currentTarget.style.transform='scale(0.93)'}
            onMouseUp={e => e.currentTarget.style.transform='scale(1)'}
            onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>
            {faceEmoji}
          </button>
          <div style={{display:'flex',alignItems:'center',gap:6,fontFamily:'monospace',fontWeight:700,fontSize:18}}>
            <span style={{minWidth:40,textAlign:'right'}}>{String(seconds).padStart(3,'0')}</span>
            <span style={{fontSize:14}}>⏱</span>
          </div>
        </div>

        {/* 보드 */}
        <div style={{display:'inline-block',padding:8,background:'#94a3b8',borderRadius:10,boxShadow:'inset 0 2px 4px rgba(0,0,0,0.15)'}}>
          {boardCells}
        </div>

        {/* 결과 메시지 */}
        {status === 'won' && (
          <div style={{marginTop:14,padding:'12px 16px',background:'#dcfce7',border:'1px solid #86efac',borderRadius:10,color:'#065f46',fontSize:14,fontWeight:700,textAlign:'center'}}>
            🎉 클리어! {seconds}초 만에 모든 지뢰를 찾았어요.
          </div>
        )}
        {status === 'lost' && (
          <div style={{marginTop:14,padding:'12px 16px',background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:10,color:'#991b1b',fontSize:14,fontWeight:700,textAlign:'center'}}>
            💥 게임 오버. 다시 도전해보세요 (🙂 버튼)
          </div>
        )}

        {/* 안내 */}
        <div style={{marginTop:10,fontSize:11,color:'#76777d',textAlign:'center'}}>
          좌클릭: 공개 · 우클릭: 깃발 · Esc: 종료
        </div>
      </div>
    </div>
  );
}
